import React, { useState, useEffect, useCallback } from 'react';
import { Search, Layers, User, Package, Bluetooth, Loader2, CheckCircle2, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getPrendasPendientesEntrega, buscarEmpleadosPorNombre, createAlistamiento, getAlistamientos } from '../services/deposito.services';
import { printBluetoothAlistamiento } from '@/utils/bluetoothPrinter';

const AlistamientoPrendas = ({ idPlanta, plantaNombre, initialCedula, onCedulaUsed }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);

  // Búsqueda
  const [modoBusqueda, setModoBusqueda] = useState('cedula');
  const [searchDocument, setSearchDocument] = useState('');
  const [busquedaNombre, setBusquedaNombre] = useState('');
  const [resultadosNombre, setResultadosNombre] = useState([]);
  const [searchingEmpleado, setSearchingEmpleado] = useState(false);
  const [empleadoNoEncontrado, setEmpleadoNoEncontrado] = useState(false);

  // Datos
  const [empleado, setEmpleado] = useState(null);
  const [prendas, setPrendas] = useState([]);
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [cantidades, setCantidades] = useState({});

  // Tabla de alistamientos
  const PAGE_SIZE = 10;
  const [alistamientos, setAlistamientos] = useState([]);
  const [totalAlistamientos, setTotalAlistamientos] = useState(0);
  const [pageAlistamientos, setPageAlistamientos] = useState(1);
  const [loadingAlistamientos, setLoadingAlistamientos] = useState(false);

  const loadAlistamientos = useCallback(async (page = 1) => {
    setLoadingAlistamientos(true);
    try {
      const response = await getAlistamientos({ idPlanta, limit: PAGE_SIZE, offset: page });
      setAlistamientos(response?.data || []);
      setTotalAlistamientos(response?.totalRecords ?? 0);
    } catch {
      // silencioso
    } finally {
      setLoadingAlistamientos(false);
    }
  }, [idPlanta]);

  useEffect(() => {
    loadAlistamientos(pageAlistamientos);
  }, [loadAlistamientos, pageAlistamientos]);

  // Auto-buscar cuando llega cédula desde Entrega
  useEffect(() => {
    if (!initialCedula) return;
    setSearchDocument(initialCedula);
    setModoBusqueda('cedula');
    cargarPrendas(initialCedula, initialCedula);
    onCedulaUsed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCedula]);

  const cargarPrendas = async (cedula, nombreLabel) => {
    setLoading(true);
    setEmpleadoNoEncontrado(false);
    try {
      const response = await getPrendasPendientesEntrega(cedula);
      const lista = response?.data || [];
      if (lista.length === 0) {
        setEmpleadoNoEncontrado(true);
        toast({ title: 'Sin prendas', description: `No hay prendas pendientes para ${nombreLabel}`, variant: 'destructive' });
        setEmpleado(null); setPrendas([]); setSeleccionadas([]); return;
      }
      setEmpleado(lista[0].empleado);
      setPrendas(lista);
      // Pre-seleccionar todas
      const ids = lista.map(p => p._id || p.id);
      setSeleccionadas(ids);
      const cantInit = {};
      lista.forEach(p => { cantInit[p._id || p.id] = p.cantidad_pendiente ?? p.cantidad ?? 0; });
      setCantidades(cantInit);
      toast({ title: 'Empleado encontrado', description: `${lista.length} tipo(s) de prenda(s) para alistar` });
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'No se pudo buscar el empleado', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const buscarPorDocumento = async () => {
    if (!searchDocument.trim()) return;
    await cargarPrendas(searchDocument.trim(), searchDocument.trim());
  };

  const buscarPorNombre = async () => {
    if (busquedaNombre.trim().length < 2) return;
    setSearchingEmpleado(true);
    setResultadosNombre([]);
    try {
      const response = await buscarEmpleadosPorNombre(busquedaNombre.trim());
      const lista = response?.data || [];
      if (lista.length === 0) setEmpleadoNoEncontrado(true);
      else setResultadosNombre(lista);
    } catch {
      setEmpleadoNoEncontrado(true);
    } finally {
      setSearchingEmpleado(false);
    }
  };

  const seleccionarEmpleado = async (emp) => {
    setResultadosNombre([]);
    setBusquedaNombre('');
    await cargarPrendas(emp.cedula, `${emp.nombre} ${emp.apellido}`);
  };

  const togglePrenda = (id) => {
    setSeleccionadas(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const getCantidad = (id, fallback) => {
    const v = cantidades[id];
    return typeof v === 'number' ? v : fallback;
  };

  const handleCantidadChange = (id, max, value) => {
    const n = Math.max(0, Math.min(Number(value) || 0, max));
    setCantidades(prev => ({ ...prev, [id]: n }));
  };

  const handleCrearAlistamiento = async () => {
    if (seleccionadas.length === 0) {
      toast({ title: 'Error', description: 'Seleccione al menos una prenda', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const prendasAlistar = prendas
        .filter(p => seleccionadas.includes(p._id || p.id))
        .map(p => {
          const id = p._id || p.id;
          const disponible = p.cantidad_pendiente ?? p.cantidad ?? 0;
          return {
            prendaRecoleccionId: id,
            recoleccionConsecutivo: p.recoleccion?.consecutivo || p.recoleccionConsecutivo || '',
            nombre: p.nombre,
            cantidad: getCantidad(id, disponible),
          };
        });

      const totalUds = prendasAlistar.reduce((s, p) => s + p.cantidad, 0);

      const body = {
        planta: { id: idPlanta, nombre: plantaNombre },
        empleado: {
          id: empleado._id || empleado.id,
          nombre: empleado.nombre,
          apellido: empleado.apellido,
          cedula: empleado.cedula,
          area: empleado.area,
        },
        prendas: prendasAlistar,
        creator: { id: user?.id, nombre: user?.name },
        observaciones: `${prendasAlistar.length} tipo(s) · ${totalUds} unidades`,
      };

      const response = await createAlistamiento(body);

      toast({ title: 'Alistamiento creado', description: `Consecutivo: ${response.data?.consecutivo}` });

      // Imprimir etiqueta Bluetooth
      await handleImprimirEtiqueta(response.data);

      // Limpiar
      setEmpleado(null); setPrendas([]); setSeleccionadas([]); setCantidades({});
      setSearchDocument(''); setBusquedaNombre('');
      setPageAlistamientos(1);
      loadAlistamientos(1);

    } catch (error) {
      toast({ title: 'Error', description: error.message || 'No se pudo crear el alistamiento', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleImprimirEtiqueta = async (data) => {
    if (!data) return;
    setPrinting(true);
    try {
      await printBluetoothAlistamiento(data);
      toast({ title: 'Etiqueta impresa', description: 'La etiqueta se imprimió correctamente.' });
    } catch (error) {
      toast({ title: 'Error de impresión', description: error.message || 'No se pudo imprimir la etiqueta.', variant: 'destructive' });
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="space-y-4">

      {/* Búsqueda */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5 text-primary" />
              Buscar Empleado
            </CardTitle>
            <div className="flex items-center border rounded-lg overflow-hidden text-sm">
              <button
                type="button"
                onClick={() => { setModoBusqueda('cedula'); setEmpleadoNoEncontrado(false); setResultadosNombre([]); }}
                className={`px-3 py-1.5 transition-colors ${modoBusqueda === 'cedula' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-800 text-muted-foreground hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >Por cédula</button>
              <button
                type="button"
                onClick={() => { setModoBusqueda('nombre'); setEmpleadoNoEncontrado(false); setResultadosNombre([]); }}
                className={`px-3 py-1.5 transition-colors ${modoBusqueda === 'nombre' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-800 text-muted-foreground hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >Por nombre</button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {modoBusqueda === 'cedula' && (
            <div className="flex gap-3">
              <div className="flex-1">
                <Label>Número de Documento</Label>
                <Input
                  placeholder="Ingrese la cédula"
                  value={searchDocument}
                  onChange={e => setSearchDocument(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && buscarPorDocumento()}
                />
              </div>
              <div className="flex items-end">
                <Button onClick={buscarPorDocumento} disabled={loading}>
                  <Search className="h-4 w-4 mr-2" />
                  {loading ? 'Buscando...' : 'Buscar'}
                </Button>
              </div>
            </div>
          )}

          {modoBusqueda === 'nombre' && (
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label>Nombre o apellido</Label>
                  <Input
                    placeholder="Ingrese nombre o apellido"
                    value={busquedaNombre}
                    onChange={e => setBusquedaNombre(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && buscarPorNombre()}
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={buscarPorNombre} disabled={searchingEmpleado || loading}>
                    <Search className="h-4 w-4 mr-2" />
                    {searchingEmpleado ? 'Buscando...' : 'Buscar'}
                  </Button>
                </div>
              </div>
              {resultadosNombre.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 dark:bg-gray-800 px-3 py-2 text-xs font-medium text-muted-foreground border-b">
                    {resultadosNombre.length} resultado(s) — seleccione uno
                  </div>
                  <div className="max-h-48 overflow-y-auto divide-y">
                    {resultadosNombre.map(emp => (
                      <button
                        key={emp._id || emp.id}
                        type="button"
                        onClick={() => seleccionarEmpleado(emp)}
                        disabled={loading}
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      >
                        <div>
                          <p className="font-medium text-sm">{emp.nombre} {emp.apellido}</p>
                          <p className="text-xs text-muted-foreground">Cédula: {emp.cedula}</p>
                        </div>
                        <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                          {emp.area?.nombre || 'Sin área'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {empleadoNoEncontrado && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                No se encontraron prendas pendientes para este empleado.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info empleado */}
      {empleado && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-4 w-4 text-primary" />
              {empleado.nombre} {empleado.apellido}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><span className="text-muted-foreground">Cédula:</span> <span className="font-medium">{empleado.cedula}</span></div>
              <div><span className="text-muted-foreground">Área:</span> <span className="font-medium">{empleado.area?.nombre || 'N/A'}</span></div>
              <div><span className="text-muted-foreground">Celular:</span> <span className="font-medium">{empleado.celular || 'N/A'}</span></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selección de prendas */}
      {prendas.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Package className="h-4 w-4 text-primary" />
                Prendas Pendientes
                <Badge variant="secondary">{prendas.length}</Badge>
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setSeleccionadas(prendas.map(p => p._id || p.id))}>
                  Seleccionar todas
                </Button>
                <Button variant="outline" size="sm" onClick={() => setSeleccionadas([])}>
                  Deseleccionar
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {prendas.map(prenda => {
                const id = prenda._id || prenda.id;
                const isSelected = seleccionadas.includes(id);
                const disponible = prenda.cantidad_pendiente ?? prenda.cantidad ?? 0;
                return (
                  <div
                    key={id}
                    onClick={() => togglePrenda(id)}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${isSelected ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                  >
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                      {isSelected && <svg className="w-2.5 h-2.5 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor"><path d="M5 13l4 4L19 7" /></svg>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{prenda.nombre}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                        <Badge variant="outline" className="text-xs font-mono">{prenda.recoleccion?.consecutivo || prenda.recoleccionConsecutivo || 'N/A'}</Badge>
                        <span>Pendiente: <strong>{disponible}</strong></span>
                      </div>
                    </div>
                    {isSelected && (
                      <div
                        className="flex items-center gap-1.5 text-xs"
                        onClick={e => e.stopPropagation()}
                      >
                        <Label className="whitespace-nowrap">Cantidad:</Label>
                        <Input
                          type="number"
                          min={1}
                          max={disponible}
                          value={getCantidad(id, disponible)}
                          onChange={e => handleCantidadChange(id, disponible, e.target.value)}
                          className="w-16 h-7 text-center text-xs"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {seleccionadas.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  {seleccionadas.length} tipo(s) seleccionado(s) ·{' '}
                  {prendas.filter(p => seleccionadas.includes(p._id || p.id))
                    .reduce((s, p) => s + getCantidad(p._id || p.id, p.cantidad_pendiente ?? p.cantidad ?? 0), 0)} unidades
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Botón crear */}
      {seleccionadas.length > 0 && (
        <div className="flex justify-end gap-3">
          <Button
            onClick={handleCrearAlistamiento}
            disabled={loading || printing}
            className="min-w-[220px] bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creando...</>
            ) : printing ? (
              <><Bluetooth className="h-4 w-4 mr-2 animate-pulse" />Imprimiendo...</>
            ) : (
              <><Layers className="h-4 w-4 mr-2" />Crear Alistamiento e Imprimir</>
            )}
          </Button>
        </div>
      )}

      {!empleado && prendas.length === 0 && !empleadoNoEncontrado && (
        <div className="text-center py-8 text-muted-foreground">
          <Layers className="h-16 w-16 mx-auto mb-4 opacity-20" />
          <p className="text-lg font-medium">Alistamiento de Prendas</p>
          <p className="text-sm mt-1">Busca un empleado para ver sus prendas pendientes</p>
        </div>
      )}

      {/* Tabla de alistamientos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Layers className="h-4 w-4 text-primary" />
            Alistamientos Registrados 
            <Badge variant="secondary">{totalAlistamientos}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingAlistamientos ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : alistamientos.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">No hay alistamientos registrados</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Consecutivo</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Empleado</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Cédula</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Área</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Prendas</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Fecha</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {alistamientos.map(ali => {
                      const totalUds = (ali.prendas || []).reduce((s, p) => s + (p.cantidad || 0), 0);
                      return (
                        <tr key={ali._id || ali.id} className="hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-2.5">
                            <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{ali.consecutivo}</span>
                          </td>
                          <td className="px-4 py-2.5 font-medium">{ali.empleado?.nombre} {ali.empleado?.apellido}</td>
                          <td className="px-4 py-2.5 text-muted-foreground">{ali.empleado?.cedula}</td>
                          <td className="px-4 py-2.5 text-muted-foreground">{ali.empleado?.area?.nombre || ali.empleado?.area || 'N/A'}</td>
                          <td className="px-4 py-2.5">
                            <div className="space-y-0.5">
                              {(ali.prendas || []).map((p, i) => (
                                <div key={i} className="flex items-center justify-between gap-3 text-xs">
                                  <span className="text-foreground">{p.nombre}</span>
                                  <span className="font-semibold tabular-nums shrink-0">{p.cantidad}</span>
                                </div>
                              ))}
                              <div className="flex items-center justify-between gap-3 text-xs border-t mt-1 pt-1 text-muted-foreground">
                                <span>Total</span>
                                <span className="font-bold tabular-nums shrink-0">{totalUds}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-muted-foreground text-xs">
                            {ali.createdAt ? new Date(ali.createdAt).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }) : 'N/A'}
                          </td>
                          <td className="px-4 py-2.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Reimprimir etiqueta"
                              onClick={() => handleImprimirEtiqueta(ali)}
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Paginador */}
              {Math.ceil(totalAlistamientos / PAGE_SIZE) > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Mostrando {((pageAlistamientos - 1) * PAGE_SIZE) + 1} - {Math.min(pageAlistamientos * PAGE_SIZE, totalAlistamientos)} de {totalAlistamientos} alistamientos
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageAlistamientos(p => p - 1)}
                      disabled={pageAlistamientos === 1 || loadingAlistamientos}
                    >
                      Anterior
                    </Button>
                    <div className="flex items-center gap-2">
                      {Array.from({ length: Math.min(5, Math.ceil(totalAlistamientos / PAGE_SIZE)) }, (_, i) => {
                        const totalPages = Math.ceil(totalAlistamientos / PAGE_SIZE);
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pageAlistamientos <= 3) {
                          pageNum = i + 1;
                        } else if (pageAlistamientos >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = pageAlistamientos - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={pageAlistamientos === pageNum ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setPageAlistamientos(pageNum)}
                            disabled={loadingAlistamientos}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageAlistamientos(p => p + 1)}
                      disabled={pageAlistamientos >= Math.ceil(totalAlistamientos / PAGE_SIZE) || loadingAlistamientos}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AlistamientoPrendas;
